<template>
  <section class="wrapper">
    <a-button
      class="button"
      type="link"
      @click="router.push('/channels/board')"
      v-if="!isViewTemplates"
    >
      <ArrowLeftOutlined /> Volver a Mis Canales
    </a-button>

    <h2>
      Plantillas de SMS

      <a-tooltip title="Crea plantilla predefinidas para que sean enviadas por tus usuarios. ">
        <InfoCircleOutlined style="margin-left: 5px" />
      </a-tooltip>
    </h2>
    <p>
      Crea plantillas predefinidas para enviar de forma unitaria o masiva por SMS.
      <a :href="DOCS_URL" target="_blank" rel="noopener noreferrer">Click aquí</a> para conocer más.
    </p>
    <!-- Controls -->
    <div class="table--controls">
      <a-form layout="inline" ref="formRef" :model="formState">
        <a-form-item>
          <a-input
            v-model:value="formState.search"
            placeholder="Buscar por nombre"
            class="form__input"
          >
            <template #prefix>
              <SearchOutlined />
            </template>
          </a-input>
        </a-form-item>

        <CampaignPicker
          v-model:value="formState.portfolio"
          with-out-entity
          default-behavior
          label=""
        />

        <a-form-item>
          <a-select
            :getPopupContainer="(triggerNode) => triggerNode.parentNode"
            v-model:value="formState.status"
            class="form__item"
            placeholder="Seleccionar"
            :options="templateStatusOptions"
          />
        </a-form-item>
      </a-form>
      <!-- Add template -->
      <a-button type="primary" @click="handleShowCreate"> Crear plantilla </a-button>
    </div>

    <a-table
      size="middle"
      class="table"
      :data-source="templateData?.results"
      :columns="smsTemplatesColumns"
      :pagination="pagination"
      :scroll="scrollStyle"
      @change="handleTableChange"
      v-if="(templateData?.results?.length > 0 && isViewTemplates) || !isViewTemplates"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'portfolios'">
          <span v-if="!getRecordPortfolios(record)?.length">Sin campañas</span>

          <template v-else>
            <a-popover placement="bottom" title="Campañas">
              <template #content>
                <ul>
                  <li v-for="portfolio in getRecordPortfolios(record)" :key="portfolio.id">
                    {{ portfolio.name }}
                  </li>
                </ul>
              </template>

              <InfoCircleOutlined style="margin-right: 4px" />
            </a-popover>

            {{
              getRecordPortfolios(record).length === 1
                ? `1 campaña`
                : `${getRecordPortfolios(record).length} campañas`
            }}
          </template>
        </template>
        <template v-if="column.key === 'queues'">
          <span v-if="record.queues.length == 0">Sin colas</span>
          <template v-else>
            <a-popover placement="bottom" title="Colas">
              <template #content>
                <ul>
                  <li v-for="queue in record.queues" :key="queue.id">
                    {{ queue.name }}
                  </li>
                </ul>
              </template>

              <InfoCircleOutlined style="margin-right: 4px" />
            </a-popover>

            {{ record.queues.length === 1 ? `1 cola` : `${record.queues.length} colas` }}
          </template>
        </template>
        <template v-if="column.key === 'active'">
          <a-switch
            size="small"
            :checked="record.status === SmsTemplateStatus.Active"
            @change="toggleStatus(record)"
          />
        </template>

        <template v-if="column.key === 'actions'">
          <div style="display: flex; gap: 16px">
            <a-tooltip>
              <template #title>Editar plantilla</template>
              <a-button type="link" class="action-button" @click="handleEdit(record)">
                <EditOutlined />
              </a-button>
            </a-tooltip>
            <a-tooltip>
              <template #title>Eliminar plantilla</template>
              <a-button type="link" class="action-button" @click="handleDelete(record.id)">
                <DeleteOutlined />
              </a-button>
            </a-tooltip>
          </div>
        </template>
      </template>
    </a-table>
    <div v-else>
      <div class="not-content">
        <span
          >Crea tus primeras plantillas preestablecidas para que sean enviadas por tus usuarios
          desde Workspace o Estrategias.</span
        >
        <a-button @click="handleShowCreate"> Crear plantilla </a-button>
      </div>
    </div>
  </section>

  <CreateModal
    v-if="createModalOpen"
    v-model:open="createModalOpen"
    :is-edit="false"
    is-email
    @update="fetchTableData"
  />

  <CreateModal
    v-if="editModalOpen"
    v-model:open="editModalOpen"
    :sms-template="templateToEdit"
    is-edit
    is-email
    @update="fetchTableData"
  />

  <RestrictionChangePlanModal
    title="Realiza un upgrade y accede a todo el poder de Beex."
    v-model:open="openRestriction"
  >
    <template #description>
      Tu plan {{ PlanTypeText[plan as PlanType] }} no incluye este módulo.
      Solicita un upgrade tu plan para potenciar tu ambiente de trabajo.
    </template>
  </RestrictionChangePlanModal>
</template>

<script setup lang="ts">
import { computed, createVNode, onMounted, reactive, ref, watch, onUnmounted } from "vue";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons-vue";
import { useRoute, useRouter } from "vue-router";
import { Modal, message } from "ant-design-vue";
import { debounce } from "lodash";

import { templateStatusOptions } from "@/app/channels/utils/selectOptions";
import { FiltersDto } from "@/@types/channels/sms/dto";
import { smsTemplatesColumns } from "@/app/channels/utils/constants";
import { SmsTemplate, SmsTemplateStatus } from "@/@types/channels/sms";
import { TABLE_PAGE_SIZE } from "@/app/shared/utils/constants";
import { smsTemplatesService } from "@/app/channels/services/SmsTemplates.service";
import { useScrollResize } from "@/app/shared/composables/useScrollResize";
import CampaignPicker from "@/app/shared/components/pickers/CampaignPicker.vue";
import CreateModal from "@/app/channels/components/modals/sms/templates/CreateModal.vue";
import { useStore } from "vuex";
import { ChannelType } from "@/app/shared/utils/enums";

import RestrictionChangePlanModal from "@/app/common/components/modals/RestrictionChangePlanModal.vue";
import { PlanType, PlanTypeText } from "@/app/common/types";
import { ActionPlanStrategy } from "@/app/common/utils/enums";
import EventBus from "@/app/shared/utils/eventBus";

const DOCS_URL = `${import.meta.env.VITE_WEB_DOC}/conexiones/plantillas/sms/`;
const route = useRoute();
const router = useRouter();
// const emit = defineEmits(["update"]);
const { scrollStyle } = useScrollResize({ headerMargin: 370 });
const formState: FiltersDto = reactive({ search: "", portfolio: "", status: "" });

const createModalOpen = ref(false);
const editModalOpen = ref(false);
const templateToEdit = ref<SmsTemplate>();

const loading = ref(false);
const templateData = ref<{ count: number; results: SmsTemplate[] }>();
const currentTablePage = ref(1);
const isViewTemplates = computed(() => route.path == "/channels/template");

const store = useStore();

const user = computed(() => store.getters["getDataUser"]);
const plan = computed(() => user.value?.change_custom_plan?.plan);
const openRestriction = ref(false);

const validateActionPlanHeader = () => {
  if (plan.value == PlanType.Voice) {
    store.commit("SET_ACTION_PLAN_HEADER", ActionPlanStrategy.Upgrade);
  } else {
    store.commit("SET_ACTION_PLAN_HEADER", null);
  }
};

EventBus.on("SHOW_RESTRICTION_PLAN", () => {
  openRestriction.value = true
});

const handleShowCreate = () => {
  if (plan.value == PlanType.Voice)
    openRestriction.value = true;
  else
    createModalOpen.value = true
}

watch(user, () => {
  validateActionPlanHeader();
})

watch(plan, () => {
  validateActionPlanHeader();
})

const fetchTableData = async () => {
  loading.value = true;

  templateData.value = await smsTemplatesService.getList({
    filters: formState,
    page: currentTablePage.value,
  });

  loading.value = false;
};

onMounted(async () => {
  validateActionPlanHeader();
  if (!route.params.channel_customer) await fetchChannelsSms();
  fetchTableData();
});

onUnmounted(() => {
  store.commit("SET_ACTION_PLAN_HEADER", null);
});

const pagination = computed(() => ({
  total: templateData.value?.count,
  current: currentTablePage.value,
  pageSize: TABLE_PAGE_SIZE,
  showSizeChanger: false,
}));

const handleTableChange = (page: { current: number }) => {
  currentTablePage.value = page.current;
  fetchTableData();
};
defineProps({
  data: {
    type: Array,
    required: true,
  },
});

watch(
  formState,
  debounce(() => {
    currentTablePage.value = 1;
    fetchTableData();
  }, 600)
);

const getRecordPortfolios = (record: SmsTemplate) => {
  return record.queues?.flatMap((q) => q.portfolios);
};

const handleEdit = (template: SmsTemplate) => {
  editModalOpen.value = true;
  templateToEdit.value = template;
};

const toggleStatus = async (template: SmsTemplate) => {
  const isActivating = template.status === SmsTemplateStatus.Active;
  const successText = !isActivating ? "reactivado" : "inactivado";
  const errorText = !isActivating ? "activar" : "inactivar";
  if (isActivating) {
    Modal.confirm({
      icon: createVNode(QuestionCircleOutlined),
      centered: true,
      title: "¿Estás seguro de inactivar esta plantilla?",
      content: "Recuerda que esta plantilla ya no podrá ser usada por ningún usuario.",
      okText: "Inactivar",
      onOk: async () => {
        try {
          await smsTemplatesService.toggleStatus(template);
          message.success(`Se ha ${successText} la plantilla correctamente`);
          fetchTableData();
        } catch (error) {
          const errorMessage =
            error?.getErrorMessage() || `No se pudo ${errorText} la plantilla correctamente`;
          message.error(errorMessage);
          console.error(error);
        }
      },
    });
  } else {
    await smsTemplatesService.toggleStatus(template);
    message.success(`Se ha ${successText} la plantilla correctamente`);
    fetchTableData();
  }
};

const handleDelete = (templateId: number) => {
  Modal.confirm({
    icon: createVNode(QuestionCircleOutlined),
    centered: true,
    title: "¿Estás seguro de eliminar esta plantilla?",
    content:
      "Recuerda que esta plantilla ya no podrá ser usada por ningún usuario y ya no se listará en tu lista de plantillas.",
    okText: "Eliminar",
    onOk: async () => {
      try {
        await smsTemplatesService.delete(templateId);
        fetchTableData();
        message.success(`Se ha eliminado la plantilla correctamente`);
      } catch (error) {
        message.error(`No se puedo eliminar la plantilla correctamente`);
        console.error(error);
      }
    },
  });
};

const fetchChannelsSms = async () => {
  await store.dispatch("fetchAllChannelsCustomers", {
    channelId: ChannelType.Sms,
  });
};
</script>

<style lang="sass" scoped>
.wrapper
  padding: 8px 20px

.button
  padding: 0

.action-button
  padding: 0
  font-size: 16px

h2
  font-weight: 600
  font-size: 16px
  color: $gray-9
  margin: 10px 0 12px

.form
  &__input
    width: 280px

  &__item
    min-width: 180px

.table
  margin-top: 18px

  &--controls
    display: flex
    justify-content: space-between

  &--button
    padding: 0

.not-content
  height: calc(100vh - 350px)
  display: flex
  align-items: center
  justify-content: center
  flex-direction: column
  span
    font-size: 22px
    width: 456px
    text-align: center
    margin-bottom: 20px
    weight: 400
    color: $gray-7
</style>
